import {HoistModel, LoadSpec, managed, TaskObserver, XH} from '@xh/hoist/core';
import {UserName} from '../../../../core/DataTypes';
import {Allocation, Portfolio, Security, TransactionBatch} from '../../../data/DataTypes';
import {runInAction} from '@xh/hoist/mobx';
import {AddAllocationDialogModel} from './AddAllocationDialogModel';
import {forEach, groupBy, isEmpty, isNumber, parseInt, sumBy} from 'lodash';
import {GridModel} from '@xh/hoist/cmp/grid';
import {required, StoreRecord} from '@xh/hoist/data';
import {actionCol, calcActionColWidth, numberEditor} from '@xh/hoist/desktop/cmp/grid';
import {fmtDateTimeSec, fmtPercent} from '@xh/hoist/format';
import {Icon} from '@xh/hoist/icon';
import {getDefaultOptions} from '../../../../core/ServiceUtils';

export class AllocationsModel extends HoistModel {
    apiTask = TaskObserver.trackLast();
    @managed gridModel = this.createGridModel();
    @managed addAllocationDialog = new AddAllocationDialogModel();
    private users: Map<number, UserName> = new Map();
    private portfolios: Map<number, Portfolio> = new Map();
    private securities: Map<number, Security> = new Map();

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        this.gridModel.clear();
        const data = [];

        try {
            if (loadSpec.isRefresh || loadSpec.loadNumber === 0) await this.initDomainDataAsync().linkTo(this.apiTask);

            const allocations = await this.fetchAllocationsAsync().linkTo(this.apiTask);

            const groupBySecurity = groupBy(allocations, 'securityId');
            forEach(groupBySecurity, (allocations, gValue) => {
                const securityId = parseInt(gValue);
                data.push({
                    id: XH.genId(),
                    type: 'group',
                    securityId,
                    userName: this.securities.get(securityId).name,
                    allocationPct: sumBy(allocations, 'allocationPct'),
                    children: allocations.map(a => ({
                        ...a,
                        type: 'item',
                        userName: this.users.get(this.portfolios.get(a.portfolioId).userId).username,
                        portfolioName: this.portfolios.get(a.portfolioId).portfolioName,
                        securityName: this.securities.get(a.securityId).name
                    }))
                });
            });
        } catch (e) {
            XH.handleException(e, {title: 'Error getting allocations'});
        }

        this.gridModel.loadData(data);
    }

    get isDirty(): boolean {
        return this.gridModel.store.isModified;
    }

    get isValid(): boolean {
        return this.gridModel.store.isValid && this.totalsValid;
    }

    get totalsValid(): boolean {
        let isValid = true;
        this.gridModel.store.allRootRecords.forEach(r => {
            if (Math.abs(r.data.allocationPct - 1) > 0.001) isValid = false;
        });

        return isValid;
    }

    refresh() {
        this.loadAsync({isRefresh: true});
    }

    revertAll() {
        this.gridModel.store.revert();
    }

    revert(record: StoreRecord) {
        this.gridModel.store.revertRecords(record);
        this.recalcRecordTotal(record);
    }

    deleteRecord(record: StoreRecord) {
        const parentRec = this.gridModel.store.allRootRecords.find(r => r.data.securityId === record.raw.securityId);
        this.gridModel.store.removeRecords(record);
        if (isEmpty(parentRec.allChildren)) this.gridModel.store.removeRecords(parentRec);
        else this.recalcRecordTotal(record);
    }

    async addNew(securityId: number) {
        const allocation = await this.addAllocationDialog.openAsync(securityId);
        if (!allocation) return;

        const parentRec = this.gridModel.store.allRootRecords.find(r => r.data.securityId === allocation.securityId),
            recId = XH.genId();
        if (parentRec) {
            this.gridModel.store.addRecords(
                {
                    ...allocation,
                    id: recId,
                    type: 'item',
                    userName: this.users.get(this.portfolios.get(allocation.portfolioId).userId).username,
                    portfolioName: this.portfolios.get(allocation.portfolioId).portfolioName,
                    securityName: this.securities.get(allocation.securityId).name
                },
                parentRec.id
            );

            this.recalcRecordTotal(this.gridModel.store.allRecords.find(r => r.id === recId));
        } else {
            const recId = XH.genId();
            const pRec = {
                id: recId,
                type: 'group',
                securityId: allocation.securityId,
                userName: this.securities.get(allocation.securityId).name,
                allocationPct: allocation.allocationPct
            };
            this.gridModel.store.addRecords(pRec);

            this.gridModel.store.addRecords(
                {
                    ...allocation,
                    id: XH.genId(),
                    type: 'item',
                    userName: this.users.get(this.portfolios.get(allocation.portfolioId).userId).username,
                    portfolioName: this.portfolios.get(allocation.portfolioId).portfolioName,
                    securityName: this.securities.get(allocation.securityId).name
                },
                recId
            );
        }
    }

    async saveAsync() {
        const changes: TransactionBatch = {
            added: this.gridModel.store.addedRecords
                .filter(r => r.data.type !== 'group')
                .map(r => ({...r.getValues(), id: isNumber(r.data.id) ? r.data.id : 0})),
            modified: this.gridModel.store.modifiedRecords
                .filter(r => r.data.type !== 'group')
                .map(r => ({...r.getValues(), id: isNumber(r.data.id) ? r.data.id : 0})),
            removed: this.gridModel.store.removedRecords.filter(r => r.data.type !== 'group').map(r => r.raw)
        };

        try {
            await XH.postJson({
                url: `${XH.getConf('svcApiUrl')}/realestate/allocations/save`,
                body: changes,
                ...getDefaultOptions()
            });

            this.loadAsync();
        } catch (e) {
            XH.handleException(e, {title: 'Error saving allocations'});
        }
    }

    private recalcRecordTotal(record: StoreRecord) {
        record.store.modifyRecords({
            id: record.parentId,
            allocationPct: sumBy(
                record.parent.children.map(r => r.data),
                'allocationPct'
            )
        });
    }

    private async initDomainDataAsync() {
        const securities = await XH.secMasterService.searchSecuritiesAsync('%');
        const portfolios = await XH.fetchService.getJson({
            url: `${XH.getConf('svcApiUrl')}/portfolios/all`,
            ...getDefaultOptions()
        });
        const users = await XH.fetchService.fetchJson({
            url: `${XH.getConf('svcApiUrl')}/users/names`,
            ...getDefaultOptions()
        });

        runInAction(() => {
            this.securities = new Map(securities.map(s => [s.id, s]));
            this.portfolios = new Map(portfolios.map(p => [p.id, p]));
            this.users = new Map(users.map(u => [u.id, u]));
        });
    }

    private fetchAllocationsAsync(): Promise<Allocation[]> {
        return XH.fetchService.getJson({
            url: `${XH.getConf('svcApiUrl')}/realestate/allocations`,
            ...getDefaultOptions()
        });
    }

    private createGridModel(): GridModel {
        return new GridModel({
            treeMode: true,
            expandLevel: -1,
            treeStyle: 'borders',
            selModel: 'disabled',
            cellBorders: true,
            colDefaults: {
                movable: false,
                hideable: false,
                resizable: false
            },
            store: {
                fields: [
                    {
                        name: 'allocationPct',
                        type: 'number',
                        rules: [
                            required,
                            {
                                check: (field, allValues) => {
                                    const value = field.value as number,
                                        type = allValues.type;
                                    if (type === 'group') {
                                        if (Math.abs(value - 1) > 0.001) return 'Total Allocation % for Security must equal 100%';
                                    } else {
                                        if (value < 0.01) return 'Allocation % must be at least 1%';
                                        if (value > 1) return 'Allocation % cannot exceed 100%';
                                    }
                                }
                            }
                        ]
                    }
                ]
            },
            columns: [
                {field: 'id', hidden: true},
                {field: 'ownerUserId', hidden: true},
                {field: 'timestamp', hidden: true},
                {field: 'type', hidden: true},
                {field: 'securityId', hidden: true},
                {field: 'securityName', hidden: true},
                {field: 'userName', headerName: 'Entity', flex: 1, isTreeColumn: true},
                {field: 'portfolioId', hidden: true},
                {field: 'portfolioName', flex: 1},
                {
                    field: 'allocationPct',
                    headerName: 'Allocation %',
                    editable: ({record}) => record.data.type !== 'group',
                    editor: params =>
                        numberEditor({
                            ...params,
                            inputProps: {stepSize: 1, scaleFactor: 100, precision: 2}
                        }),
                    align: 'right',
                    renderer: v => fmtPercent(v),
                    setValueFn: params => {
                        const {value, column, record} = params;

                        record.store.modifyRecords({
                            id: record.id,
                            [column.field]: value
                        });

                        this.recalcRecordTotal(record);
                    },
                    width: 100
                },
                {field: 'updated', renderer: v => fmtDateTimeSec(v), width: 190},
                {field: 'updatedBy', width: 150},
                {
                    ...actionCol,
                    width: calcActionColWidth(2),
                    actions: [
                        {
                            icon: Icon.undo(),
                            intent: 'primary',
                            displayFn: ({record}) => ({
                                disabled: !record.isModified,
                                hidden: record.data.type === 'group'
                            }),
                            actionFn: ({record}) => this.revert(record)
                        },
                        {
                            icon: Icon.delete(),
                            intent: 'danger',
                            displayFn: ({record}) => ({
                                icon: record.isAdd ? Icon.close() : Icon.delete(),
                                hidden: record.data.type === 'group'
                            }),
                            actionFn: ({record}) => this.deleteRecord(record)
                        },
                        {
                            icon: Icon.add(),
                            intent: 'success',
                            displayFn: ({record}) => ({
                                hidden: record.data.type !== 'group'
                            }),
                            actionFn: ({record}) => this.addNew(record.data.securityId)
                        }
                    ]
                }
            ],
            rowClassFn: ({data}) => {
                if (data.type === 'group') {
                    return 'allocation-group-row';
                }
            }
        });
    }
}
